import React, { useMemo, useState, useCallback } from 'react';
import { formatCurrency } from '../../utils/formatters';
import Pagination from '../Pagination';

const CustomerDetailView = ({ customer, sales, onBack, openModal }) => {
    
    const [expandedSaleId, setExpandedSaleId] = useState(null);
    const [historyFilters, setHistoryFilters] = useState({ search: '', status: 'all' });
    const [historyCurrentPage, setHistoryCurrentPage] = useState(1);
    const [historyItemsPerPage, setHistoryItemsPerPage] = useState(5);
    const [historySort, setHistorySort] = useState({ key: 'saleDate', direction: 'desc' });
    const [receivablesSort, setReceivablesSort] = useState({ key: 'dueDate', direction: 'asc' });

    const handleHistorySort = useCallback((key) => {
        setHistorySort(prevSort => ({
            key,
            direction: prevSort.key === key && prevSort.direction === 'asc' ? 'desc' : 'asc'
        }));
    }, []);

    const handleReceivablesSort = useCallback((key) => {
        setReceivablesSort(prevSort => ({
            key,
            direction: prevSort.key === key && prevSort.direction === 'asc' ? 'desc' : 'asc'
        }));
    }, []);

    const handleHistoryItemsPerPageChange = useCallback((value) => {
        setHistoryItemsPerPage(Number(value));
        setHistoryCurrentPage(1);
    }, []);
    
    const handleToggleExpand = (saleId) => {
        setExpandedSaleId(prevId => (prevId === saleId ? null : saleId));
    };

    const customerSales = useMemo(() => {
        if (!customer || !sales) return [];
        return sales.filter(sale => sale.customerId === customer.id);
    }, [customer, sales]);

    const financials = useMemo(() => {
        if (!customerSales.length) {
            return { totalSpent: 0, totalPaid: 0, outstandingBalance: 0, pendingInstallments: [] };
        }
        
        const totalSpent = customerSales.reduce((acc, sale) => acc + (sale.totalAmount || 0), 0);
        const totalPaid = customerSales.reduce((acc, sale) => {
            const paidForThisSale = sale.installments?.reduce((pAcc, inst) => pAcc + (inst.paidAmount || 0), 0) || 0;
            return acc + paidForThisSale;
        }, 0);
        const outstandingBalance = totalSpent - totalPaid;

        const pendingInstallments = customerSales
            .flatMap(sale => 
                (sale.installments || [])
                    .filter(inst => inst.status === 'Pendente')
                    .map(inst => ({ 
                        ...inst, 
                        saleId: sale.id, 
                        productName: (sale.items && sale.items.length > 0) ? sale.items[0].productName : sale.productName,
                        remainingOnInstallment: inst.amount - (inst.paidAmount || 0)
                    }))
            );
        return { totalSpent, totalPaid, outstandingBalance, pendingInstallments };
    }, [customerSales]);

    const sortedPendingInstallments = useMemo(() => {
        const items = [...financials.pendingInstallments];
        items.sort((a, b) => {
            const valA = a[receivablesSort.key] || '';
            const valB = b[receivablesSort.key] || '';
            if (valA instanceof Date && valB instanceof Date) {
                return receivablesSort.direction === 'asc' ? valA.getTime() - valB.getTime() : valB.getTime() - valA.getTime();
            }
            if (typeof valA === 'string') {
                return receivablesSort.direction === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
            }
            if (typeof valA === 'number') {
                return receivablesSort.direction === 'asc' ? valA - valB : valB - valA;
            }
            return 0;
        });
        return items;
    }, [financials.pendingInstallments, receivablesSort]);


    const filteredAndSortedCustomerSales = useMemo(() => {
        let items = [...customerSales].map(sale => ({
            ...sale,
            representativeName: (sale.items && sale.items.length > 0) ? sale.items[0].productName : sale.productName,
            totalQuantity: sale.totalQuantity ?? sale.quantity
        }));

        if (historyFilters.search) {
            items = items.filter(sale => 
                sale.representativeName.toLowerCase().includes(historyFilters.search.toLowerCase())
            );
        }
        if (historyFilters.status !== 'all') {
            items = items.filter(sale => sale.status === historyFilters.status);
        }

        items.sort((a, b) => {
            const valA = a[historySort.key] || '';
            const valB = b[historySort.key] || '';
            
            if (valA instanceof Date && valB instanceof Date) {
                return historySort.direction === 'asc' ? valA.getTime() - valB.getTime() : valB.getTime() - valA.getTime();
            }
            if (typeof valA === 'string') {
                return historySort.direction === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
            }
            if (typeof valA === 'number') {
                return historySort.direction === 'asc' ? valA - valB : valB - valA;
            }
            return 0;
        });

        return items;
    }, [customerSales, historyFilters, historySort]);

    const paginatedCustomerSales = useMemo(() => {
        const startIndex = (historyCurrentPage - 1) * historyItemsPerPage;
        // CORREÇÃO: Usando a variável correta `historyItemsPerPage`
        return filteredAndSortedCustomerSales.slice(startIndex, startIndex + historyItemsPerPage);
    }, [filteredAndSortedCustomerSales, historyCurrentPage, historyItemsPerPage]);

    const getStatusClass = (status) => {
        switch (status) {
            case 'Concluída': return 'bg-green-100 text-green-800';
            case 'Pendente': return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    if (!customer) {
        return (
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-[#F2DFDB]">
                <p>Cliente não encontrado ou não selecionado.</p>
                <button onClick={onBack} className="mt-4 custom-button py-2 px-4">Voltar</button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
             <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-2 gap-4">
                <div className="w-full text-center sm:text-left">
                    <button onClick={onBack} className="text-sm font-semibold text-gray-600 hover:text-gray-900 flex items-center gap-2 mb-2 w-full sm:w-auto justify-center sm:justify-start">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                        Voltar para a lista de Clientes
                    </button>
                    <h2 className="text-2xl font-bold text-gray-800">{customer.name}</h2>
                </div>
                 <div className="text-sm w-full bg-gray-50 p-4 rounded-lg space-y-1 text-center sm:text-right">
                    <p><strong className="text-gray-600">Telefone:</strong> {customer.phone || 'Não informado'}</p>
                    <p><strong className="text-gray-600">Email:</strong> {customer.email || 'Não informado'}</p>
                    <p><strong className="text-gray-600">Endereço:</strong> {customer.address || 'Não informado'}</p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-green-50 p-6 rounded-xl border border-green-200 text-center md:text-left">
                    <h3 className="text-sm font-medium text-green-700">Faturamento Total</h3>
                    <p className="text-3xl font-bold text-green-900 mt-2">{formatCurrency(financials.totalSpent)}</p>
                </div>
                <div className="bg-blue-50 p-6 rounded-xl border border-blue-200 text-center md:text-left">
                    <h3 className="text-sm font-medium text-blue-700">Total Pago</h3>
                    <p className="text-3xl font-bold text-blue-900 mt-2">{formatCurrency(financials.totalPaid)}</p>
                </div>
                <div className="bg-red-50 p-6 rounded-xl border border-red-200 text-center md:text-left">
                    <h3 className="text-sm font-medium text-red-700">Saldo Devedor</h3>
                    <p className="text-3xl font-bold text-red-900 mt-2">{formatCurrency(financials.outstandingBalance)}</p>
                </div>
            </div>

            <div className="bg-white p-4 sm:p-6 rounded-2xl shadow-sm border border-[#F2DFDB]">
                <h3 className="text-lg font-bold text-gray-800 mb-4">Próximos Recebimentos</h3>
                
                {/* START: Mobile View (Receivables) */}
                <div className="space-y-4 md:hidden">
                    {sortedPendingInstallments.length > 0 ? (
                        sortedPendingInstallments.map(inst => (
                            <div key={`${inst.saleId}-${inst.installmentNumber}`} className="border rounded-lg p-4 space-y-3">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <h3 className="font-bold text-gray-800">{inst.productName}</h3>
                                        <p className="text-sm text-gray-500">Vence: {inst.dueDate ? new Date(inst.dueDate).toLocaleDateString('pt-BR') : 'N/A'}</p>
                                    </div>
                                    <p className="font-bold text-lg text-red-600 flex-shrink-0 ml-2">{formatCurrency(inst.remainingOnInstallment)}</p>
                                </div>
                                <div className="flex justify-end items-center pt-3 border-t">
                                    <button className="action-btn text-xl" onClick={() => openModal('addPayment', sales.find(s => s.id === inst.saleId))} title="Registrar Pagamento">💰</button>
                                </div>
                            </div>
                        ))
                    ) : (
                        <p className="p-4 text-center text-gray-500">Nenhuma parcela pendente.</p>
                    )}
                </div>
                {/* END: Mobile View (Receivables) */}
                
                {/* START: Desktop View (Receivables) */}
                <div className="overflow-x-auto hidden md:block">
                    <table className="w-full text-left">
                        <thead className="border-b-2 border-[#F2DFDB]">
                            <tr>
                                <th className="p-4 text-sm font-semibold text-gray-700 sortable-header" onClick={() => handleReceivablesSort('dueDate')}>Vencimento {receivablesSort.key === 'dueDate' && (receivablesSort.direction === 'asc' ? '▲' : '▼')}</th>
                                <th className="p-4 text-sm font-semibold text-gray-700 sortable-header" onClick={() => handleReceivablesSort('productName')}>Venda {receivablesSort.key === 'productName' && (receivablesSort.direction === 'asc' ? '▲' : '▼')}</th>
                                <th className="p-4 text-sm font-semibold text-gray-700 sortable-header" onClick={() => handleReceivablesSort('remainingOnInstallment')}>Valor a Pagar {receivablesSort.key === 'remainingOnInstallment' && (receivablesSort.direction === 'asc' ? '▲' : '▼')}</th>
                                <th className="p-4 text-sm font-semibold text-gray-700 text-center">Ação</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedPendingInstallments.length > 0 ? (
                                sortedPendingInstallments.map(inst => (
                                    <tr key={`${inst.saleId}-${inst.installmentNumber}`} className="border-b border-[#F2DFDB] hover:bg-[#F4EFEC]">
                                        <td className="p-4 text-gray-600">{inst.dueDate ? new Date(inst.dueDate).toLocaleDateString('pt-BR') : 'N/A'}</td>
                                        <td className="p-4 font-medium text-gray-800">{inst.productName}</td>
                                        <td className="p-4 text-gray-800 font-medium">{formatCurrency(inst.remainingOnInstallment)}</td>
                                        <td className="p-4 text-center">
                                            <button className="action-btn" onClick={() => openModal('addPayment', sales.find(s => s.id === inst.saleId))} title="Registrar Pagamento">💰</button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="4" className="p-4 text-center text-gray-500">Nenhuma parcela pendente para este cliente.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                {/* END: Desktop View (Receivables) */}
            </div>

            <div className="bg-white p-4 sm:p-6 rounded-2xl shadow-sm border border-[#F2DFDB]">
                <h3 className="text-lg font-bold text-gray-800 mb-4">Histórico de Compras</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <input type="text" value={historyFilters.search} onChange={e => setHistoryFilters({...historyFilters, search: e.target.value})} placeholder="🔎 Pesquisar por produto..." className="w-full px-4 py-2 bg-white border border-[#D7BFBD] rounded-lg" />
                    <select value={historyFilters.status} onChange={e => setHistoryFilters({...historyFilters, status: e.target.value})} className="w-full px-4 py-2 bg-white border border-[#D7BFBD] rounded-lg">
                        <option value="all">Todos os Status</option>
                        <option value="Concluída">Concluída</option>
                        <option value="Pendente">Pendente</option>
                    </select>
                </div>

                {/* START: Mobile View (History) */}
                <div className="space-y-4 md:hidden">
                    {paginatedCustomerSales.length > 0 ? (
                        paginatedCustomerSales.map(sale => {
                            const isExpanded = expandedSaleId === sale.id;
                            return (
                                <div key={sale.id} className="border rounded-lg p-4 space-y-3">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <h3 className="font-bold text-gray-800">{sale.representativeName}{sale.items && sale.items.length > 1 ? ` (+${sale.items.length - 1})` : ''}</h3>
                                            <p className="text-sm text-gray-500">{sale.saleDate ? sale.saleDate.toLocaleDateString('pt-BR') : 'N/A'}</p>
                                        </div>
                                        <p className="font-bold text-lg text-gray-800 flex-shrink-0 ml-2">{formatCurrency(sale.totalAmount)}</p>
                                    </div>
                                    <div className="flex justify-between items-center pt-3 border-t">
                                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusClass(sale.status)}`}>{sale.status || 'N/A'}</span>
                                        <div className="flex items-center gap-2">
                                            {sale.items && sale.items.length > 1 && (<button onClick={() => handleToggleExpand(sale.id)} className="action-btn text-xl">{isExpanded ? '▲' : '▼'}</button>)}
                                        </div>
                                    </div>
                                    {isExpanded && (
                                        <div className="pt-2 mt-2 border-t text-sm space-y-1">
                                            <h4 className="font-semibold text-gray-600">Itens:</h4>
                                            {sale.items.map((item, index) => (
                                                <p key={index} className="flex justify-between text-gray-700">
                                                    <span>{item.quantity}x {item.productName}</span>
                                                    <span>{formatCurrency(item.unitPrice * item.quantity)}</span>
                                                </p>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )
                        })
                    ) : (
                        <p className="p-4 text-center text-gray-500">Nenhuma compra encontrada.</p>
                    )}
                </div>
                {/* END: Mobile View (History) */}
                
                {/* START: Desktop View (History) */}
                <div className="overflow-x-auto hidden md:block">
                    <table className="w-full text-left">
                        <thead className="border-b-2 border-[#F2DFDB]">
                            <tr>
                                <th className="p-4 w-8"></th>
                                <th className="p-4 text-sm font-semibold text-gray-700 sortable-header" onClick={() => handleHistorySort('saleDate')}>Data {historySort.key === 'saleDate' && (historySort.direction === 'asc' ? '▲' : '▼')}</th>
                                <th className="p-4 text-sm font-semibold text-gray-700 sortable-header" onClick={() => handleHistorySort('representativeName')}>Produto(s) {historySort.key === 'representativeName' && (historySort.direction === 'asc' ? '▲' : '▼')}</th>
                                <th className="p-4 text-sm font-semibold text-gray-700 sortable-header text-center" onClick={() => handleHistorySort('totalQuantity')}>Qtd. Total {historySort.key === 'totalQuantity' && (historySort.direction === 'asc' ? '▲' : '▼')}</th>
                                <th className="p-4 text-sm font-semibold text-gray-700 sortable-header" onClick={() => handleHistorySort('totalAmount')}>Valor Total {historySort.key === 'totalAmount' && (historySort.direction === 'asc' ? '▲' : '▼')}</th>
                                <th className="p-4 text-sm font-semibold text-gray-700 sortable-header" onClick={() => handleHistorySort('status')}>Status {historySort.key === 'status' && (historySort.direction === 'asc' ? '▲' : '▼')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedCustomerSales.length > 0 ? (
                                paginatedCustomerSales.map(sale => {
                                    const isExpanded = expandedSaleId === sale.id;
                                    return (
                                        <React.Fragment key={sale.id}>
                                            <tr className="border-b border-[#F2DFDB] hover:bg-[#F4EFEC]">
                                                <td className="p-4 text-center">
                                                    {sale.items && sale.items.length > 1 && (
                                                        <button onClick={() => handleToggleExpand(sale.id)} className="text-lg">{isExpanded ? '▲' : '▼'}</button>
                                                    )}
                                                </td>
                                                <td className="p-4 text-gray-600">{sale.saleDate ? sale.saleDate.toLocaleDateString('pt-BR') : 'N/A'}</td>
                                                <td className="p-4 font-medium text-gray-800">{sale.representativeName}</td>
                                                <td className="p-4 text-gray-800 font-medium text-center">{sale.totalQuantity}</td>
                                                <td className="p-4 text-gray-800 font-medium">{formatCurrency(sale.totalAmount)}</td>
                                                <td className="p-4">
                                                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusClass(sale.status)}`}>{sale.status || 'N/A'}</span>
                                                </td>
                                            </tr>
                                            {isExpanded && (
                                                <tr className="bg-gray-50">
                                                    <td></td>
                                                    <td colSpan="5" className="p-4">
                                                        <div className="space-y-2">
                                                            <h4 className="font-semibold">Itens da Venda:</h4>
                                                            {sale.items.map((item, index) => (
                                                                <div key={index} className="flex justify-between text-sm ml-4">
                                                                    <span>{item.quantity}x {item.productName}</span>
                                                                    <span>{formatCurrency(item.unitPrice * item.quantity)}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan="6" className="p-4 text-center text-gray-500">Nenhuma compra encontrada para os filtros selecionados.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                {/* END: Desktop View (History) */}

                <Pagination
                    totalItems={filteredAndSortedCustomerSales.length}
                    itemsPerPage={historyItemsPerPage}
                    currentPage={historyCurrentPage}
                    onPageChange={setHistoryCurrentPage}
                    onItemsPerPageChange={handleHistoryItemsPerPageChange}
                />
            </div>
        </div>
    );
};

export default CustomerDetailView;
